import { addBusinessDays } from 'date-fns';
import { parseLocalDate } from '@/utils/dates';
import { formatDateForInput, calculateBusinessDaysBetween } from '@/utils/dateUtils';

export interface TaskDependency {
  task_id: number;
  depends_on_task_id: number;
}

export interface ShiftableTask {
  id: number;
  start_date: string;
  end_date: string;
}

/** Maps each task ID to the IDs of tasks that depend on it (downstream tasks). */
export type DependencyGraph = Record<number, number[]>;

/**
 * Build an adjacency list from dependency rows.
 * For each row { task_id, depends_on_task_id }, the entry
 * graph[depends_on_task_id] contains task_id — meaning task_id depends on
 * depends_on_task_id, so moving depends_on_task_id should shift task_id.
 */
export function buildDependencyGraph(dependencies: TaskDependency[]): DependencyGraph {
  const graph: DependencyGraph = {};
  for (const dep of dependencies) {
    const predecessorId = dep.depends_on_task_id;
    if (!graph[predecessorId]) {
      graph[predecessorId] = [];
    }
    graph[predecessorId].push(dep.task_id);
  }
  return graph;
}

export interface ShiftedTask {
  id: number;
  start_date: string;
  end_date: string;
}

/**
 * Calculate new dates for all tasks that (transitively) depend on movedTask.
 * Uses a topological sort (Kahn's algorithm) so each task is processed after
 * ALL of its affected predecessors have been resolved — this ensures that in
 * a diamond graph (A→B, A→C, B→D, C→D) task D starts after the *later* of
 * B's and C's new end dates, not just whichever BFS reached it first.
 *
 * The moved task itself is not included in the returned array.
 */
export function shiftDependentTasks(
  movedTask: ShiftableTask,
  allTasks: ShiftableTask[],
  graph: DependencyGraph,
): ShiftedTask[] {
  const taskMap = new Map<number, ShiftableTask>(allTasks.map((t) => [t.id, t]));

  // Phase 1: collect all transitively reachable dependent IDs via BFS.
  const reachable = new Set<number>();
  {
    const bfsQ: number[] = [movedTask.id];
    const bfsSeen = new Set([movedTask.id]);
    while (bfsQ.length > 0) {
      const id = bfsQ.shift()!;
      for (const depId of graph[id] ?? []) {
        if (!bfsSeen.has(depId) && taskMap.has(depId)) {
          bfsSeen.add(depId);
          reachable.add(depId);
          bfsQ.push(depId);
        }
      }
    }
  }
  if (reachable.size === 0) return [];

  // Phase 2: build predecessor lists restricted to the affected subgraph
  // (movedTask ∪ reachable). Predecessors outside this set have fixed dates
  // and don't gate topological ordering.
  const allAffected = new Set([movedTask.id, ...reachable]);
  const predecessors = new Map<number, number[]>();
  for (const id of reachable) predecessors.set(id, []);
  for (const predId of allAffected) {
    for (const depId of graph[predId] ?? []) {
      if (reachable.has(depId)) predecessors.get(depId)!.push(predId);
    }
  }

  // Phase 3: Kahn's topological BFS — process a node only after all of its
  // affected predecessors have been resolved.
  const effectiveEndDate = new Map<number, Date>();
  effectiveEndDate.set(movedTask.id, parseLocalDate(movedTask.end_date));

  const inDegree = new Map<number, number>(
    [...predecessors.entries()].map(([id, preds]) => [id, preds.length]),
  );
  const topoQueue: number[] = [];
  // movedTask is already resolved (effectiveEndDate is set). Pre-process it so
  // nodes that depend only on movedTask get their in-degree decremented to 0
  // and enter the queue — without this the queue starts empty.
  for (const depId of graph[movedTask.id] ?? []) {
    if (reachable.has(depId)) {
      const newDeg = (inDegree.get(depId) ?? 1) - 1;
      inDegree.set(depId, newDeg);
      if (newDeg === 0) topoQueue.push(depId);
    }
  }
  // Also enqueue any remaining nodes already at degree 0 (shouldn't normally occur,
  // but guards against graphs where the only predecessor is outside allAffected).
  for (const [id, deg] of inDegree) {
    if (deg === 0 && !topoQueue.includes(id)) topoQueue.push(id);
  }

  const result: ShiftedTask[] = [];

  while (topoQueue.length > 0) {
    const depId = topoQueue.shift()!;

    // New start = next business day after the *latest* predecessor's effective end.
    let maxPredEnd = new Date(0);
    for (const predId of predecessors.get(depId) ?? []) {
      const predEnd = effectiveEndDate.get(predId);
      if (predEnd && predEnd > maxPredEnd) maxPredEnd = predEnd;
    }

    const depTask = taskMap.get(depId);
    if (depTask) {
      const depStart = parseLocalDate(depTask.start_date);
      const depEnd = parseLocalDate(depTask.end_date);
      const duration = calculateBusinessDaysBetween(depStart, depEnd);

      if (duration <= 0) {
        console.warn(`[shiftDependentTasks] task ${depId} has invalid duration (${duration}) — end_date may be before start_date; treating as 1-day`);
      }

      const newStart = addBusinessDays(maxPredEnd, 1);
      const newEnd = addBusinessDays(newStart, Math.max(0, duration - 1));

      effectiveEndDate.set(depId, newEnd);
      result.push({
        id: depId,
        start_date: formatDateForInput(newStart),
        end_date: formatDateForInput(newEnd),
      });
    }

    // Decrement in-degree for downstream dependents; enqueue when ready.
    for (const nextId of graph[depId] ?? []) {
      if (reachable.has(nextId)) {
        const newDeg = (inDegree.get(nextId) ?? 1) - 1;
        inDegree.set(nextId, newDeg);
        if (newDeg === 0) topoQueue.push(nextId);
      }
    }
  }

  return result;
}
